import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';
import styles from '../DepositModal/DepositModal.module.css';
import axiosClient from '../../api/axiosClient';
import { showPaymentError } from '../../utils/kycErrorToast';
import { useCurrency } from '../../app/CurrencyContext';

interface Props {
  method: 'fiat' | 'crypto';
  onClose: () => void;
  onSuccess?: () => void;
}

interface Wallet {
  currency: string;
  available: number;
  locked: number;
  held?: number;
  withdrawable?: number;
}

function money(amount: number, currency: string) {
  const formatted = amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return currency === 'INR'
    ? `₹${formatted}`
    : `${currency} ${formatted}`;
}

const WithdrawalModal: React.FC<Props> = ({
  method,
  onClose,
  onSuccess,
}) => {
  const {
    toInr,
    currencies: adminCurrencies,
    inrPerUnit,
  } = useCurrency();

  const [currency, setCurrency] = useState('INR');
  const [amount, setAmount] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [upiId, setUpiId] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [note, setNote] = useState('');

  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [withdrawalRate, setWithdrawalRate] = useState<number | null>(null);
  // The admin charge is set by the admin (System Settings), so it is loaded, never hard-coded.
  const [adminChargePct, setAdminChargePct] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const savingRef = useRef(false);
  const isCrypto = method === 'crypto';

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const [balanceResult, settingsResult] = await Promise.all([
          axiosClient.get('/funds/balance'),
          axiosClient.get('/settings'),
        ]);

        if (!active) return;

        const charge = Number(settingsResult?.data?.settings?.withdrawal_admin_charge_pct);
        if (!Number.isFinite(charge) || charge < 0 || charge >= 100) {
          throw new Error('The withdrawal admin charge is unavailable.');
        }
        setAdminChargePct(charge);

        setWallets(balanceResult.data.wallets || []);

        if (method === 'crypto') {
          const rate = Number(
            settingsResult?.data?.settings?.usdt_withdrawal_rate
          );

          if (!Number.isFinite(rate) || rate <= 0) {
            throw new Error('Withdrawal conversion rate is unavailable.');
          }

          setWithdrawalRate(rate);
        }
      } catch (error: any) {
        if (!active) return;

        setLoadError(
          error.response?.data?.message
          || error.message
          || 'Could not load withdrawal details.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [method, retryKey]);

  const validAmount = /^\d{1,12}(\.\d{1,2})?$/.test(amount.trim())
    && Number(amount) > 0;

  const amountNum = validAmount ? Number(amount) : 0;
  const payoutCurrency = isCrypto ? 'INR' : currency;

  const convertedAmount = isCrypto
    ? amountNum * (withdrawalRate || 0)
    : amountNum;

  const grossMinor = Number.isFinite(convertedAmount)
    ? Math.round(convertedAmount * 100)
    : 0;

  const grossValid = Number.isSafeInteger(grossMinor)
    && grossMinor > 0
    && grossMinor <= 99999999999999;

  const grossAmount = grossValid ? grossMinor / 100 : 0;
  const chargeMinor = grossValid
    ? Math.floor((grossMinor * (adminChargePct ?? 0) + 50) / 100)
    : 0;

  const chargeAmount = chargeMinor / 100;
  const netAmount = grossValid
    ? (grossMinor - chargeMinor) / 100
    : 0;

  const totalAvailableInr = wallets.reduce(
    (total, wallet) => total + toInr(
      // Only the withdrawable part counts: Portal principal and bonuses inside their 30-day wait are held.
      Number(wallet.withdrawable ?? wallet.available),
      wallet.currency
    ),
    0
  );

  const targetRate = isCrypto ? 1 : Number(inrPerUnit(currency));
  const conversionAvailable = Number.isFinite(targetRate)
    && targetRate > 0
    && Number.isFinite(totalAvailableInr);

  const availableAmount = conversionAvailable
    ? Math.max(0, totalAvailableInr / targetRate)
    : 0;

  const exceedsBalance = grossValid
    && conversionAvailable
    && grossAmount > availableAmount + 0.000001;

  const closeModal = () => {
    if (!savingRef.current) onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (savingRef.current) return;

    if (loading || loadError) {
      toast.error('Wait for withdrawal details to load successfully.');
      return;
    }

    if (!validAmount || !grossValid) {
      toast.error('Enter a valid amount with up to two decimal places.');
      return;
    }

    if (!conversionAvailable) {
      toast.error('Currency conversion is unavailable.');
      return;
    }

    if (
      !isCrypto
      && !adminCurrencies.some((item) => item.code === currency)
    ) {
      toast.error('Select an available currency.');
      return;
    }

    if (isCrypto && !withdrawalRate) {
      toast.error('Withdrawal conversion rate is unavailable.');
      return;
    }

    if (exceedsBalance) {
      toast.error('The requested amount exceeds your withdrawable balance.');
      return;
    }

    const holder = accountHolderName.trim();
    const bankAccount = accountNumber.trim();
    const bankIfsc = ifscCode.trim().toUpperCase();
    const upi = upiId.trim();
    const wallet = walletAddress.trim();

    if (!upi && !(bankAccount && bankIfsc)) {
      toast.error('Provide a UPI ID or Account Number and IFSC.');
      return;
    }

    if (isCrypto && !/^0x[a-fA-F0-9]{40}$/.test(wallet)) {
      toast.error('Enter a valid BNB Chain wallet address.');
      return;
    }

    const inrEquivalent = isCrypto
      ? grossAmount
      : toInr(grossAmount, currency);

    if (
      !Number.isFinite(inrEquivalent)
      || inrEquivalent <= 0
      || inrEquivalent > 999999999999.99
    ) {
      toast.error('The INR conversion amount is invalid.');
      return;
    }

    savingRef.current = true;
    setSaving(true);

    try {
      const payload = {
        type: 'withdrawal',
        amount: grossAmount.toFixed(2),
        amountInrEquivalent: inrEquivalent.toFixed(2),
        currency: payoutCurrency,
        expectedChargePct: adminChargePct,
        paymentMethodType: isCrypto
          ? 'Crypto Withdrawal'
          : 'Bank/UPI Withdrawal',
        accountHolderName: holder,
        accountNumber: bankAccount,
        ifscCode: bankIfsc,
        upiId: upi,
        note: note.trim(),
        ...(isCrypto
          ? {
              exchangeRate: withdrawalRate,
              cryptoAmount: amountNum,
              walletAddress: wallet,
            }
          : {}),
      };

      await axiosClient.post('/funds', payload);
    } catch (error: any) {
      savingRef.current = false;
      setSaving(false);
      showPaymentError(error, 'Could not submit the withdrawal.');
      return;
    }

    savingRef.current = false;
    setSaving(false);

    toast.success('Withdrawal request submitted for review.');
    window.dispatchEvent(new Event('wallet:refresh'));
    onClose();
    onSuccess?.();
  };

  return (
    <div className={styles.overlay} onClick={closeModal}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="withdrawal-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          maxWidth: 540,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div className={styles.header}>
          <div>
            <h2 id="withdrawal-title">
              {isCrypto ? 'USDT to INR Withdrawal' : 'Cash Withdrawal'}
            </h2>
            <p>
              Review your payout details and the admin charge.
            </p>
          </div>

          <button
            type="button"
            className={styles.closeBtn}
            onClick={closeModal}
            disabled={saving}
            aria-label="Close withdrawal form"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <fieldset
            disabled={saving || loading || !!loadError}
            style={{ border: 0, padding: 22, margin: 0, minWidth: 0 }}
          >
            {loading && <p role="status">Loading withdrawal details…</p>}

            {!isCrypto && (
              <>
                <label className={styles.label} htmlFor="withdraw-currency">
                  Currency
                </label>

                <select
                  id="withdraw-currency"
                  className={styles.textInput}
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                >
                  {!adminCurrencies.some((item) => item.code === currency) && (
                    <option value={currency} disabled>
                      Select currency
                    </option>
                  )}

                  {adminCurrencies.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.code}
                    </option>
                  ))}
                </select>
              </>
            )}

            {isCrypto && (
              <div className={styles.rateRow} style={{ marginBottom: 16 }}>
                <span>Withdrawal conversion rate</span>
                <b>
                  {withdrawalRate
                    ? `1 USDT = ${money(withdrawalRate, 'INR')}`
                    : 'Unavailable'}
                </b>
              </div>
            )}

            <div className={styles.dropzoneHint} style={{ marginBottom: 16 }}>
              Withdrawable balance:
              {' '}
              <b>
                {loading
                  ? 'Loading…'
                  : conversionAvailable
                    ? money(availableAmount, payoutCurrency)
                    : 'Unavailable'}
              </b>
              <br />
              Portal investments and bonuses still inside their 30-day wait are locked.
            </div>

            <label className={styles.label} htmlFor="withdraw-amount">
              {isCrypto ? 'Amount to Convert (USDT)' : `Amount (${currency})`}
            </label>

            <input
              id="withdraw-amount"
              className={styles.textInput}
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder={isCrypto ? 'e.g. 50' : 'e.g. 1750'}
              required
            />

            {amount.trim() && !validAmount && (
              <p role="alert" style={{ color: '#e2534a' }}>
                Enter a positive amount with up to two decimal places.
              </p>
            )}

            {exceedsBalance && (
              <p role="alert" style={{ color: '#e2534a' }}>
                The requested amount exceeds your withdrawable balance.
              </p>
            )}

            <div
              className={styles.receiveBox}
              style={{ marginTop: 16, marginBottom: 20 }}
            >
              <span>Requested payout</span>
              <b>{money(grossAmount, payoutCurrency)}</b>

              <span style={{ marginTop: 10 }}>
                Admin charge ({adminChargePct ?? '—'}%)
              </span>
              <b>{money(chargeAmount, payoutCurrency)}</b>

              <span style={{ marginTop: 10 }}>You will receive</span>
              <b>{money(netAmount, payoutCurrency)}</b>

              <span className={styles.receiveSub}>
                The charge is deducted from your requested amount.
                Your balance reservation is {money(grossAmount, payoutCurrency)}.
              </span>
            </div>

            <label className={styles.label} htmlFor="withdraw-holder">
              Account Holder Name
            </label>
            <input
              id="withdraw-holder"
              className={styles.textInput}
              value={accountHolderName}
              onChange={(event) => setAccountHolderName(event.target.value)}
              maxLength={150}
              placeholder="As per bank records"
            />

            <label className={styles.label} htmlFor="withdraw-upi">
              UPI ID
            </label>
            <input
              id="withdraw-upi"
              className={styles.textInput}
              value={upiId}
              onChange={(event) => setUpiId(event.target.value)}
              maxLength={80}
              placeholder="yourname@bank"
            />

            <label className={styles.label} htmlFor="withdraw-account">
              Account Number
            </label>
            <input
              id="withdraw-account"
              className={styles.textInput}
              type="text"
              value={accountNumber}
              onChange={(event) => setAccountNumber(event.target.value)}
              maxLength={40}
              placeholder="Bank account number"
            />

            <label className={styles.label} htmlFor="withdraw-ifsc">
              IFSC Code
            </label>
            <input
              id="withdraw-ifsc"
              className={styles.textInput}
              value={ifscCode}
              onChange={(event) => setIfscCode(
                event.target.value.toUpperCase()
              )}
              maxLength={20}
              placeholder="e.g. SBIN0001234"
            />

            <div className={styles.dropzoneHint} style={{ marginBottom: 16 }}>
              Provide either a UPI ID or Account Number and IFSC.
            </div>

            {isCrypto && (
              <>
                <label className={styles.label} htmlFor="withdraw-wallet">
                  Wallet Address (BNB Chain)
                </label>
                <input
                  id="withdraw-wallet"
                  className={styles.textInput}
                  value={walletAddress}
                  onChange={(event) => setWalletAddress(event.target.value)}
                  maxLength={42}
                  placeholder="0x..."
                  required
                />
                <div
                  className={styles.dropzoneHint}
                  style={{ marginBottom: 16 }}
                >
                  Wallet address is recorded with this request.
                  Payment is settled in INR using the bank/UPI details above.
                </div>
              </>
            )}

            <label className={styles.label} htmlFor="withdraw-note">
              Note (optional)
            </label>
            <textarea
              id="withdraw-note"
              className={styles.textInput}
              rows={3}
              maxLength={2000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Additional details"
            />

            <p className={styles.dropzoneHint} style={{ marginTop: 16 }}>
              Confirm your receiving details before submitting.
              Your request will be reviewed by the admin.
            </p>

            <button
              type="submit"
              className={styles.confirmBtn}
              disabled={
                saving
                || loading
                || !!loadError
                || !validAmount
                || !grossValid
                || !conversionAvailable
                || exceedsBalance
                || (isCrypto && !withdrawalRate)
              }
            >
              {saving ? 'Submitting…' : 'Request Withdrawal'}
            </button>
          </fieldset>

          {loadError && (
            <div style={{ padding: '0 22px 22px' }}>
              <p role="alert" style={{ color: '#e2534a' }}>
                {loadError}
              </p>
              <button
                type="button"
                className={styles.confirmBtn}
                onClick={() => setRetryKey((value) => value + 1)}
              >
                Retry
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default WithdrawalModal;